import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Save, Link2, UserPlus, MessageSquare, Mail, ClipboardCopy } from 'lucide-react';
import AgentLayout from '../components/AgentLayout';
import AdvancedQuoteFields, { emptyQuoteData, buildQuotePayload } from '../components/AdvancedQuoteFields';
import { createLead } from '../utils/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COVERAGE_MAP = { whole_life: 'life', term: 'life', iul: 'life', annuity: 'life', final_expense: 'final_expense', medicare: 'medicare', health: 'health' };

const QuotingTool = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(emptyQuoteData());
  const [saving, setSaving] = useState(false);
  const [share, setShare] = useState({ name: '', phone: '', email: '' });
  const token = localStorage.getItem('agentToken');
  const agentInfo = JSON.parse(localStorage.getItem('agentInfo') || '{}');
  const agentName = agentInfo.fullName || 'David Ashley';

  const publicUrl = `${window.location.origin}/quote-tool`;

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('Public quote-tool link copied to clipboard');
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(buildMessage());
      toast.success('Full message copied — paste it into Google Voice or any app');
    } catch (e) {
      toast.error('Could not copy message');
    }
  };

  const buildMessage = () => {
    const hi = share.name.trim() ? `Hi ${share.name.trim()}, ` : 'Hi, ';
    return `${hi}here's a quick and secure form to help me find you the best insurance coverage. It only takes a couple of minutes: ${publicUrl}\n\n— ${agentName}, Purposefully Covered Insurance Group, (912) 376-9006`;
  };

  const textLink = async () => {
    const phone = share.phone.replace(/[^\d+]/g, '');
    if (!phone) { toast.error('Enter a prospect phone first'); return; }
    try {
      await axios.post(`${API}/quote-invite`, {
        email: share.email.trim(), phone: share.phone.trim(), name: share.name.trim(),
        quoteLink: publicUrl, agentName, channel: 'text',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Outreach logged to CRM — opening Messages…');
    } catch (e) { /* still open Messages even if CRM logging fails */ }
    window.location.href = `sms:${phone}?body=${encodeURIComponent(buildMessage())}`;
  };

  const emailLink = async () => {
    const to = share.email.trim();
    if (!to) { toast.error('Enter a prospect email first'); return; }
    try {
      const res = await axios.post(`${API}/quote-invite`, {
        email: to, phone: share.phone.trim(), name: share.name.trim(),
        quoteLink: publicUrl, agentName, channel: 'email',
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) toast.success(`Branded quote invite emailed to ${to} · logged to CRM`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email invite');
    }
  };


  const validate = () => {
    if (!data.firstName.trim() || !data.lastName.trim()) { toast.error('First and last name are required'); return false; }
    if (!data.phone.trim()) { toast.error('Phone number is required'); return false; }
    if (!data.state.trim()) { toast.error('State is required'); return false; }
    if (!data.zipCode.trim()) { toast.error('ZIP code is required'); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildQuotePayload(data);
      const res = await createLead({
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        phone: payload.phone,
        streetAddress: payload.streetAddress,
        city: payload.city,
        state: payload.state,
        zipCode: payload.zipCode,
        coverageInterest: COVERAGE_MAP[data.coverageType] || 'life',
        source: 'quote_tool',
        formType: 'Advanced Quote',
        notes: 'Created via CRM Quoting Tool.',
        quoteDetails: payload.quoteDetails,
        quotes: [payload.quoteDetails],
      }, token);
      if (res.success) {
        toast.success('Contact created with quote details');
        navigate('/admin/leads');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AgentLayout title="Quoting Tool">
      <div className="max-w-3xl mx-auto" data-testid="quoting-tool-page">
        <div className="bg-brand-blue/5 border border-brand-blue/20 rounded-xl p-4 mb-6" data-testid="share-panel">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
            <UserPlus size={18} className="text-brand-blue" />
            Send your public quote link to a prospect
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <input value={share.name} onChange={(e) => setShare({ ...share, name: e.target.value })} placeholder="Prospect name (optional)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid="share-name" />
            <input value={share.phone} onChange={(e) => setShare({ ...share, phone: e.target.value })} placeholder="Phone (for text)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid="share-phone" />
            <input value={share.email} onChange={(e) => setShare({ ...share, email: e.target.value })} placeholder="Email (for email)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid="share-email" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={textLink} className="flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-brand-orange transition" data-testid="share-text-btn">
              <MessageSquare size={16} /> Text Link
            </button>
            <button onClick={emailLink} className="flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-brand-orange transition" data-testid="share-email-btn">
              <Mail size={16} /> Email Link
            </button>
            <button onClick={copyLink} className="flex items-center gap-2 px-4 py-2 bg-white border border-brand-blue text-brand-blue rounded-lg text-sm font-semibold hover:bg-brand-blue hover:text-white transition" data-testid="copy-public-link">
              <Link2 size={16} /> Copy Link
            </button>
            <button onClick={copyMessage} className="flex items-center gap-2 px-4 py-2 bg-white border border-brand-blue text-brand-blue rounded-lg text-sm font-semibold hover:bg-brand-blue hover:text-white transition" data-testid="copy-message-btn">
              <ClipboardCopy size={16} /> Copy Message
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2"><b>Text Link</b> opens your device&apos;s Messages app (sent from your phone) with the message pre-filled. <b>Email Link</b> sends the prospect a branded email invitation. <b>Copy Message</b> copies the full personalized text + link to your clipboard — paste it into Google Voice or any app.</p>
        </div>

        <AdvancedQuoteFields data={data} setData={setData} />

        <button onClick={handleSave} disabled={saving}
          className="mt-6 w-full bg-brand-orange text-white font-bold py-4 rounded-xl text-lg hover:bg-orange-600 transition disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="quoting-tool-save">
          {saving ? <><Loader2 className="animate-spin" size={20} /> Saving…</> : <><Save size={20} /> Create Contact</>}
        </button>
      </div>
    </AgentLayout>
  );
};

export default QuotingTool;
